import React from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { Science as ScienceIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useExperimentalFeatures } from '../contexts/ExperimentalFeaturesContext';
import { authService } from '../services/AuthService';

/** Renders children only when experimental features are on (header Experimental control). */
export const WhenExperimentalFeaturesEnabled: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { enabled } = useExperimentalFeatures();
  if (!enabled) return null;
  return <>{children}</>;
};

const ExperimentalFeaturesToggle: React.FC = () => {
  const { t } = useTranslation();
  const { enabled, toggle } = useExperimentalFeatures();
  const userRoles = authService.getRoles();
  const isAdmin = userRoles.includes('ADMIN');
  const isDev = process.env.NODE_ENV === 'development';

  // Production: admins only. Local dev: anyone logged in can toggle (tutors/hosts need tutor nav QA).
  if (!isAdmin && !isDev) {
    return null;
  }

  return (
    <Tooltip title={enabled ? t('experimentalFeatures.disable') : t('experimentalFeatures.enable')}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: { xs: 0.5, sm: 0.75 },
          px: { xs: 1, sm: 1.5 },
          py: { xs: 0.5, sm: 0.75 },
          borderRadius: '20px',
          border: '1px solid',
          borderColor: enabled ? 'primary.main' : 'divider',
          backgroundColor: enabled ? 'action.selected' : 'background.paper',
          minHeight: { xs: '28px', sm: '40px' },
          cursor: 'pointer',
          WebkitTapHighlightColor: 'rgba(0, 0, 0, 0.1)',
          touchAction: 'manipulation',
          '&:hover': {
            backgroundColor: 'action.hover',
          },
        }}
        onClick={toggle}
      >
        <ScienceIcon 
          sx={{ 
            fontSize: { xs: '0.9rem', sm: '1rem' },
            color: enabled ? 'primary.main' : 'text.secondary'
          }} 
        />
        <Typography
          variant="body2"
          sx={{
            fontSize: { xs: '0.75rem', sm: '0.875rem' },
            color: enabled ? 'primary.main' : 'text.secondary',
            fontWeight: enabled ? 600 : 500,
            display: { xs: 'none', sm: 'block' },
            whiteSpace: 'nowrap'
          }}
        >
          {t('experimentalFeatures.label')}
        </Typography>
      </Box>
    </Tooltip>
  );
};

export default ExperimentalFeaturesToggle;
